import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'horizontal') {
    return (
      <article className="flex flex-col sm:flex-row gap-4 p-4 bg-background-50 rounded-lg border border-background-200 hover:border-accent-500/30 transition-colors group cursor-pointer">
        <Link to={`/article/${article.id}`} className="sm:w-48 sm:flex-shrink-0 overflow-hidden rounded-md">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-40 sm:h-32 object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <Link
              to={`/category/${article.category}`}
              className="text-xs px-2 py-0.5 rounded-full bg-accent-500/10 text-accent-500 whitespace-nowrap hover:bg-accent-500/20 transition-colors"
            >
              {article.categoryName}
            </Link>
            {article.isHot && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-foreground-950 text-accent-500 whitespace-nowrap">HOT</span>
            )}
          </div>
          <Link to={`/article/${article.id}`}>
            <h3 className="text-base font-semibold text-foreground-900 group-hover:text-accent-500 transition-colors line-clamp-2 mb-1.5">
              {article.title}
            </h3>
          </Link>
          <p className="text-sm text-foreground-500 line-clamp-2 mb-2">{article.excerpt}</p>
          <div className="flex items-center gap-3 text-xs text-foreground-400">
            <span>{article.publishDate}</span>
            <span>{article.author}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
            </span>
          </div>
        </div>
      </article>
    );
  }

  if (variant === 'compact') {
    return (
      <article className="flex items-start gap-3 py-3 border-b border-background-200 last:border-0 group cursor-pointer">
        <Link to={`/article/${article.id}`} className="w-20 h-16 flex-shrink-0 overflow-hidden rounded-md">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </Link>
        <div className="flex-1 min-w-0">
          <Link to={`/article/${article.id}`}>
            <h4 className="text-sm font-medium text-foreground-800 group-hover:text-accent-500 transition-colors line-clamp-2">
              {article.title}
            </h4>
          </Link>
          <div className="flex items-center gap-2 mt-1 text-xs text-foreground-400">
            <span>{article.publishDate}</span>
            <span className="flex items-center gap-0.5">
              <i className="ri-eye-line"></i>
              {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
            </span>
          </div>
        </div>
      </article>
    );
  }

  // Default card
  return (
    <article className="bg-background-50 rounded-lg border border-background-200 overflow-hidden hover:border-accent-500/30 transition-colors group cursor-pointer">
      <Link to={`/article/${article.id}`} className="block overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
        />
      </Link>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Link
            to={`/category/${article.category}`}
            className="text-xs px-2 py-0.5 rounded-full bg-accent-500/10 text-accent-500 whitespace-nowrap hover:bg-accent-500/20 transition-colors"
          >
            {article.categoryName}
          </Link>
          {article.isHot && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-foreground-950 text-accent-500 whitespace-nowrap">HOT</span>
          )}
        </div>
        <Link to={`/article/${article.id}`}>
          <h3 className="text-base font-semibold text-foreground-900 group-hover:text-accent-500 transition-colors line-clamp-2 mb-2">
            {article.title}
          </h3>
        </Link>
        <p className="text-sm text-foreground-500 line-clamp-2 mb-3">{article.excerpt}</p>
        <div className="flex items-center justify-between text-xs text-foreground-400">
          <span>{article.publishDate}</span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
          </span>
        </div>
      </div>
    </article>
  );
}